import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import "./styles.css";
import { ReactTable, VehicleMap } from "../../Components/Molecules";
import { DEVICE_INSTALLED, DEVICE_IN_STOCK_HEADERS, ALL_DEVICES_HEADERS } from "../../Library/tableHeaders";
import { Button } from "@chakra-ui/react";
import { DownloadIcon } from "../../Assets/images/svgs";
import { Breadcrumb, Title } from "../../Components/Atoms";
import { getDevices } from "../../APIs";
import { PING_TEST_STATUS, PING_TEST_STATUS_VAL, SELLER_DES, SPN_IDS_MAP } from "../../Library/constants";
import { Link, useSearchParams } from "react-router-dom";
import PATHS from "../../Routes/paths";
import { getLocalisedDate } from "../../Library/datetime";
import { jsonToCSVConvertor } from "../../Library/jsonUtils";
import { StoreContext } from "../../Library/contexts";

const Devices = () => {
  const [devices, setDevices] = useState([]);
  const DESIGNATION = JSON.parse(window.localStorage.getItem("des"));
  const [searchParams] = useSearchParams();
  const dealerId = useRef(searchParams.get("idx"));
  const dealerName = useRef(decodeURIComponent(searchParams.get("dnm")));
  const { isLiveTrackingEnabled } = useContext(StoreContext);

  const inStock = searchParams.get("inStock");
  const installed = searchParams.get("installed");

  const downloadDevicesData = (data, type) => {
    if (!data.length) return;

    const mapping = {
      installed: {
        args: "Sr No, Device IMEI, IMSI No, Carrier, Order ID, Delivery Date, Vehicle(Chassis Number)",
        additionalFields: (device) => ({
          "Vehicle(Chassis Number)": device.chNum,
        }),
        fileName: "Devices_Mapped_",
      },
      inStock: {
        args: "Sr No, Device IMEI, IMSI No, Carrier, Order ID, Delivery Date, Status",
        additionalFields: (device) => ({
          Status: device.status,
        }),
        fileName: "Devices_In_Stock_",
      },
      all: {
        args: "Sr No, Device IMEI, IMSI No, Carrier, Order ID, Delivery Date, Status, Ping Test Status, Vehicle(Chassis Number), Date of Mapping",
        additionalFields: (device) => ({
          Status: device.status,
          "Ping Test Status": device.pingTest,
          "Vehicle(Chassis Number)": device.chNum || "-",
          "Date of Mapping": device.dm,
        }),
        fileName: "All_Devices_",
      },
    };

    const { args, additionalFields, fileName } = mapping[type];

    const sendToExcel = data.map((device, idx) => ({
      "Sr No": idx + 1,
      "Device IMEI": device.imei,
      "IMSI No": device.imsi || "-",
      "Carrier": device.spnId ? SPN_IDS_MAP[device.spnId] : "-",
      "Order ID": device.orderId,
      "Delivery Date": new Date(device.deliveredAt).toLocaleDateString(
        "default",
        {
          day: "2-digit",
          month: "long",
          year: "numeric",
        }
      ),
      ...additionalFields(device),
    }));

    const sendData = sendToExcel.map((exceldata) =>
      args.split(",").reduce((mem, arg) => {
        arg = arg.trim();
        mem[arg] = exceldata[arg];
        return mem;
      }, {})
    );

    if (sendData.length)
      jsonToCSVConvertor(sendData, "", true, fileName + Date.now(), false);
  };

  const getTitle = (stock, installed) => {
    if (stock && installed) return "All Devices";
    if (stock) return "Devices not mapped";
    if (installed) return "Devices Mapped";
  };

  const getHeaders = () => {
    if (inStock && installed) return ALL_DEVICES_HEADERS;
    if (inStock) return DEVICE_IN_STOCK_HEADERS;
    if (installed) return DEVICE_INSTALLED;
    return {};
  };

  const columns = useMemo(
    () =>
      Object.values(getHeaders()).map((header) => {
        return {
          Header: header.nm,
          accessor: header.key,
          disableSortBy: !header.sorting,
          Cell: (row) => {
            if (row.column.id === "imei") {
              let link = `${PATHS.INVENTORY_OVERVIEW}/${PATHS.SPECIFIC_DEVICE}?imei=${row.value}`;
              if (DESIGNATION === SELLER_DES)
                link = `${PATHS.SHOW_DEALERS}/${PATHS.SPECIFIC_DEVICE}?imei=${row.value}`;
              if (dealerId.current) link += `&idx=${dealerId.current}`;
              if (inStock) link += `&inStock=true`;
              if (installed) link += `&installed=true`;
              return (
                <Link className="text-blue imei-link" to={`${link}&isDm=true`}>
                  {row.value || "-"}
                </Link>
              );
            }

            if (row.column.id === "deliveredAt") {
              const date = new Date(row.value);
              return (
                <span className="dc">{`${date.getDate()} ${date.toLocaleString(
                  "default",
                  {
                    month: "long",
                  }
                )} ${date.getFullYear()}`}</span>
              );
            }

            return <span className="cell-value dc">{row.value || "-"}</span>;
          },
        };
      }),
    [DESIGNATION, inStock, installed]
  );

  const getBreadcrumbPaths = () => {
    let breadcrumbPaths = [
      {
        idx: 1,
        name: `${
          DESIGNATION === SELLER_DES ? dealerName.current : "Inventory Management"
        }`,
        link:
          DESIGNATION === SELLER_DES
            ? `${PATHS.SHOW_DEALERS}/${PATHS.SPECIFIC_DEALER}?idx=${dealerId.current}`
            : PATHS.INVENTORY_OVERVIEW,
        isCurrentPage: false,
      },
      {
        idx: 2,
        name: `${getTitle(inStock, installed)}`,
        link: "",
        isCurrentPage: true,
      },
    ];
    if (DESIGNATION === SELLER_DES) breadcrumbPaths.unshift({ idx: 0, name: "Dealer Management", link: PATHS.SHOW_DEALERS, });

    return breadcrumbPaths;
  }

  useEffect(() => {
    getDevices(
      DESIGNATION === SELLER_DES ? dealerId.current : null,
      inStock,
      installed,
      (err, data) => {
        if (err) {
          console.error("Error in getDevices");
          return;
        }

        data.forEach((dev) => {
          if (dev.deliveredAt)
            dev.deliveredAt = getLocalisedDate(dev.deliveredAt, true);

          if (dev.dm) {
            dev.dm = getLocalisedDate(dev.dm, true);
            dev.status = "Mapped";
          }
          else {
            dev.dm = "-";
            dev.status = "Unmapped";
          }

          if (dev.ptStat && dev.ptStat.st) {
            if (dev.ptStat.st === PING_TEST_STATUS_VAL["In Progress"] && dev.vid) {
              dev.pingTest = PING_TEST_STATUS[1];
            }
            else dev.pingTest = PING_TEST_STATUS[dev.ptStat.st];
          } else dev.pingTest = "Test Pending";
        });
        setDevices(data);
      }
    );
  }, [DESIGNATION, inStock, installed]);

  const handleDownload = (data) => {
    if (inStock && installed) {
      downloadDevicesData(data, "all");
    } else if (inStock) {
      downloadDevicesData(data, "inStock");
    } else if (installed) {
      downloadDevicesData(data, "installed");
    }
  };

  return (
    <div className="devices">
      <div className="bread-crumb-container">
        <Breadcrumb paths={getBreadcrumbPaths()} />
      </div>

      <div className="title-container">
        <Title>{`${getTitle(inStock, installed)} (${devices.length})`}</Title>
        <Button
          className="download-btn"
          onClick={() => handleDownload(devices)}
        >
          <DownloadIcon />
        </Button>
      </div>
      {devices && devices.length && installed && !inStock && isLiveTrackingEnabled ? <div className="mapped-devs-map">
        <VehicleMap mappedDevPage={true} mapContainerClass={"mapped-devices-loc"} vids={devices.map(dev => dev.vid)} />
      </div> : null}
      <ReactTable
        columns={columns}
        data={devices}
        showSearch={true}
        pageSize={10}
      />
    </div>
  );
};

export default Devices;
